import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/Button';
import { TranslationNamespace } from 'i18n';

// Single attempt — no OTP resend; on a wrong code the order fails, so the customer only gets the
// input + a Cancel action.
type Props = {
  onSubmit: (confirmCode: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

// Mirror of the backend SubmitConfirmCodeDto minimum (MIN_CONFIRM_CODE_LENGTH).
const MIN_OTP_LENGTH = 3;

const onlyDigits = (value: string) => value.replace(/\D/g, '');

// Same input styling as the form-builder TextField so the OTP form matches the SCI theme.
const INPUT_CLASS_NAME =
  'tw-block tw-w-full tw-min-w-0 tw-h-14 tw-px-4 tw-text-sm tw-rounded-xl tw-bg-primary-input md:tw-h-20 md:tw-px-6 md:tw-rounded-2xl tw-text-primary-input md:tw-text-xl md:tw-leading-8';

export const EcomOtpForm: React.FC<Props> = ({
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.ecom_otp_form',
  });

  const [confirmCode, setConfirmCode] = useState('');

  const isValid = confirmCode.length >= MIN_OTP_LENGTH;

  const handleSubmit = useCallback(() => {
    if (!isValid || isLoading) {
      return;
    }
    onSubmit(confirmCode);
  }, [isValid, isLoading, confirmCode, onSubmit]);

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-3 md:tw-gap-4">
      <h3 className="tw-text-lg tw-font-bold tw-text-primary md:tw-text-2xl">
        {t('title')}
      </h3>
      <p className="tw-text-secondary">{t('hint')}</p>
      <input
        className={INPUT_CLASS_NAME}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label={t('code')}
        placeholder={t('code')}
        value={confirmCode}
        onChange={(e) => setConfirmCode(onlyDigits(e.target.value))}
      />
      <Button
        className="tw-mt-2 disabled:!tw-text-secondary"
        onClick={handleSubmit}
        disabled={!isValid || isLoading}
      >
        {t('submit')}
      </Button>
      <Button color="secondary" onClick={onCancel} disabled={isLoading}>
        {t('cancel')}
      </Button>
    </div>
  );
};
